import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  ApiError,
  getIntakeStripeLink,
  type IntakeStripeLinkResponse,
} from '../../../lib/api.js';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.4 — Step 3 "Stripe Connect" for the prospect-facing intake portal.
 *
 * Fields mirror `intakeStep3Schema` in `packages/config/src/intake-steps.ts`:
 *
 *   stripeAccountId              — minted by /stripe-link
 *   accountLinkGeneratedAt       — stamped by /stripe-link
 *   accountLinkLastClickedAt?    — stamped when the prospect clicks the link
 *   onboardingCompleteAt?        — set by a future Stripe webhook roundtrip
 *   capabilities { cardPayments, transfers, achPayments? }
 *   requirementsCurrentlyDue / requirementsEventuallyDue
 *   payoutsEnabled / chargesEnabled
 *   statementDescriptor          — prospect input (5–22 chars, alnum+spaces)
 *   tenantConfirmedMOR: true     — prospect checkbox (money-transmission terms)
 *
 * v1 without a webhook roundtrip: when /stripe-link succeeds the component
 * seeds `capabilities`, `requirements*`, `payoutsEnabled`, `chargesEnabled`
 * to the known initial-state values of a freshly-minted Express account
 * (`'pending'` capabilities, empty requirement arrays, `false` enablements).
 * A follow-on ticket ports the Stripe webhook to overwrite those with the
 * live Stripe lifecycle values. That keeps Save & continue unblocked for
 * prospects while leaving the schema's integrity intact.
 */

const STATEMENT_MIN = 5;
const STATEMENT_MAX = 22;
const STATEMENT_REGEX = /^[A-Za-z0-9 ]+$/;
const STRIPE_ACCOUNT_ID_REGEX = /^acct_[A-Za-z0-9]+$/;

type CapabilityState = 'active' | 'pending' | 'inactive';

type Step3Draft = {
  stripeAccountId: string;
  accountLinkGeneratedAt: string;
  accountLinkLastClickedAt: string;
  statementDescriptor: string;
  tenantConfirmedMOR: boolean;
  capabilities: {
    cardPayments: CapabilityState | '';
    transfers: CapabilityState | '';
    achPayments: CapabilityState | '';
  };
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
};

type LinkState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string; expiresAt: string }
  | { kind: 'error'; message: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function boolField(value: unknown): boolean {
  return value === true;
}

function readCapabilityState(value: unknown): CapabilityState | '' {
  return value === 'active' || value === 'pending' || value === 'inactive'
    ? value
    : '';
}

function readStep3(draft: Record<string, unknown>): Step3Draft {
  const caps = isRecord(draft.capabilities) ? draft.capabilities : {};
  return {
    stripeAccountId: stringField(draft.stripeAccountId),
    accountLinkGeneratedAt: stringField(draft.accountLinkGeneratedAt),
    accountLinkLastClickedAt: stringField(draft.accountLinkLastClickedAt),
    statementDescriptor: stringField(draft.statementDescriptor),
    tenantConfirmedMOR: boolField(draft.tenantConfirmedMOR),
    capabilities: {
      cardPayments: readCapabilityState(caps.cardPayments),
      transfers: readCapabilityState(caps.transfers),
      achPayments: readCapabilityState(caps.achPayments),
    },
    payoutsEnabled: boolField(draft.payoutsEnabled),
    chargesEnabled: boolField(draft.chargesEnabled),
  };
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function fieldErrorId(field: string): string {
  return `intake-step3-${field}-error`.replace(/[^a-z0-9-]/gi, '-');
}

function describeStatementDescriptorError(value: string): string | null {
  if (value === '') return null;
  if (value.length < STATEMENT_MIN) {
    return `Must be at least ${STATEMENT_MIN} characters.`;
  }
  if (value.length > STATEMENT_MAX) {
    return `Must be at most ${STATEMENT_MAX} characters.`;
  }
  if (!STATEMENT_REGEX.test(value)) {
    return 'Letters, numbers, and spaces only.';
  }
  return null;
}

/** Test seam — matches the shape of `getIntakeStripeLink`. */
export type IntakeStripeLinkFetcher = (
  apiUrl: string,
  token: string,
  body: { returnUrl: string; refreshUrl?: string },
) => Promise<IntakeStripeLinkResponse>;

export type Step3StripeConnectProps = IntakeShellRenderProps & {
  /** Test seam — overrides the network call to /stripe-link. */
  getLink?: IntakeStripeLinkFetcher;
  /** Test seam — overrides `window.location.href` for the return/refresh URLs. */
  returnUrl?: string;
};

export function Step3StripeConnect({
  stepDraft,
  onChange,
  issues,
  apiUrl,
  token,
  getLink = getIntakeStripeLink,
  returnUrl,
}: Step3StripeConnectProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const value = useMemo(() => readStep3(stepDraft), [stepDraft]);
  const [linkState, setLinkState] = useState<LinkState>({ kind: 'idle' });

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  const statementDescriptorClientError = describeStatementDescriptorError(
    value.statementDescriptor,
  );
  const statementDescriptorServerError = serverErrorFor('statementDescriptor');
  const morServerError = serverErrorFor('tenantConfirmedMOR');
  const stripeAccountServerError = serverErrorFor('stripeAccountId');

  const accountReady =
    value.stripeAccountId !== ''
    && STRIPE_ACCOUNT_ID_REGEX.test(value.stripeAccountId);
  const canMintLink =
    !accountReady
      ? value.tenantConfirmedMOR
        && statementDescriptorClientError === null
        && value.statementDescriptor.length >= STATEMENT_MIN
      : true;

  const onStatementDescriptorChange = (
    e: ChangeEvent<HTMLInputElement>,
  ): void => {
    onChange({ statementDescriptor: e.target.value });
  };

  const onMorChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange({ tenantConfirmedMOR: e.target.checked });
  };

  const mintLink = async (): Promise<void> => {
    if (!canMintLink) return;
    setLinkState({ kind: 'loading' });
    const url =
      returnUrl
      ?? (typeof window !== 'undefined' ? window.location.href : apiUrl);
    try {
      const res = await getLink(apiUrl, token, {
        returnUrl: url,
        refreshUrl: url,
      });
      const patch: Record<string, unknown> = {
        stripeAccountId: res.stripeAccountId,
        accountLinkGeneratedAt: res.generatedAt,
      };
      // Seed Stripe-lifecycle fields to their known initial values so
      // the schema is satisfiable pre-webhook. A later ticket overwrites
      // them from Stripe's Account events (`account.updated`).
      if (!isRecord(stepDraft.capabilities)) {
        patch.capabilities = {
          cardPayments: 'pending' as const,
          transfers: 'pending' as const,
        };
      }
      if (stepDraft.payoutsEnabled === undefined) patch.payoutsEnabled = false;
      if (stepDraft.chargesEnabled === undefined) patch.chargesEnabled = false;
      if (!Array.isArray(stepDraft.requirementsCurrentlyDue)) {
        patch.requirementsCurrentlyDue = [];
      }
      if (!Array.isArray(stepDraft.requirementsEventuallyDue)) {
        patch.requirementsEventuallyDue = [];
      }
      onChange(patch);
      setLinkState({
        kind: 'ready',
        url: res.url,
        expiresAt: res.expiresAt,
      });
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.status === 503
            ? 'Stripe is not available in this environment yet. Contact support.'
            : `Stripe link request failed (${e.status}). ${e.message}`
          : 'Could not reach Stripe — check your connection and retry.';
      setLinkState({ kind: 'error', message });
    }
  };

  const onStripeLinkClick = (): void => {
    onChange({ accountLinkLastClickedAt: new Date().toISOString() });
  };

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 3 — Stripe Connect"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Connect a Stripe Express account so we can process bookings, deposits,
        and payouts on your behalf. You remain the merchant of record; Ferro
        is the platform facilitator.
      </p>

      <section
        className="ferro-field"
        aria-labelledby="intake-step3-descriptor-heading"
      >
        <h2
          id="intake-step3-descriptor-heading"
          className="ferro-field__label"
        >
          Statement descriptor
        </h2>
        <p className="ferro-muted">
          This is what customers see on their card statement. 5–22 characters,
          letters, numbers, and spaces only.
        </p>
        <label className="ferro-field">
          <span className="ferro-field__label">Descriptor</span>
          <input
            type="text"
            name="statementDescriptor"
            className="ferro-field__input"
            inputMode="text"
            autoCapitalize="characters"
            maxLength={STATEMENT_MAX}
            required
            value={value.statementDescriptor}
            onChange={onStatementDescriptorChange}
            placeholder="HAPPY TAIL HAPPY DOG"
            aria-invalid={
              statementDescriptorClientError || statementDescriptorServerError
                ? true
                : undefined
            }
            aria-describedby={
              statementDescriptorClientError || statementDescriptorServerError
                ? fieldErrorId('statementDescriptor')
                : undefined
            }
            data-testid="intake-step3-statement-descriptor"
          />
          {statementDescriptorClientError ? (
            <span
              id={fieldErrorId('statementDescriptor')}
              role="alert"
              className="ferro-form__error"
            >
              {statementDescriptorClientError}
            </span>
          ) : null}
          {statementDescriptorServerError && !statementDescriptorClientError ? (
            <span
              id={fieldErrorId('statementDescriptor')}
              role="alert"
              className="ferro-form__error"
            >
              {statementDescriptorServerError}
            </span>
          ) : null}
        </label>
      </section>

      <section
        className="ferro-field"
        aria-labelledby="intake-step3-mor-heading"
      >
        <h2 id="intake-step3-mor-heading" className="ferro-field__label">
          Merchant-of-record terms
        </h2>
        <p className="ferro-muted">
          You confirm you'll process payments directly via Stripe and will
          accept Stripe's{' '}
          <a
            href="https://stripe.com/connect-account/legal/full"
            target="_blank"
            rel="noreferrer"
          >
            Connect Express service agreement
          </a>
          . Ferro is the platform facilitator, not the merchant of record.
        </p>
        <label className="ferro-field ferro-intake__checkbox">
          <input
            type="checkbox"
            name="tenantConfirmedMOR"
            checked={value.tenantConfirmedMOR}
            onChange={onMorChange}
            aria-invalid={morServerError ? true : undefined}
            aria-describedby={morServerError ? fieldErrorId('mor') : undefined}
            data-testid="intake-step3-mor-checkbox"
          />
          <span className="ferro-field__label">
            I confirm the merchant-of-record terms.
          </span>
        </label>
        {morServerError ? (
          <span
            id={fieldErrorId('mor')}
            role="alert"
            className="ferro-form__error"
          >
            {morServerError}
          </span>
        ) : null}
      </section>

      <section
        className="ferro-field"
        aria-labelledby="intake-step3-link-heading"
      >
        <h2 id="intake-step3-link-heading" className="ferro-field__label">
          Stripe onboarding link
        </h2>
        {accountReady ? (
          <p
            className="ferro-muted"
            data-testid="intake-step3-account-id"
          >
            Connected account:{' '}
            <code>{value.stripeAccountId}</code>
            {value.accountLinkGeneratedAt
              ? ` · link generated ${new Date(value.accountLinkGeneratedAt).toLocaleString()}`
              : null}
          </p>
        ) : (
          <p className="ferro-muted">
            Once you confirm the terms and enter a descriptor, we'll mint a
            one-time onboarding link to Stripe. The link expires after a short
            window; come back to this step to generate a fresh one.
          </p>
        )}
        {stripeAccountServerError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step3-account-servererror"
          >
            {stripeAccountServerError}
          </span>
        ) : null}

        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          onClick={() => void mintLink()}
          disabled={!canMintLink || linkState.kind === 'loading'}
          data-testid="intake-step3-mint-link"
        >
          {linkState.kind === 'loading'
            ? 'Minting link…'
            : accountReady
              ? 'Generate a new Stripe link'
              : 'Connect with Stripe'}
        </button>

        {linkState.kind === 'ready' ? (
          <p className="ferro-intake__stripeLink">
            <a
              href={linkState.url}
              target="_blank"
              rel="noreferrer"
              onClick={onStripeLinkClick}
              data-testid="intake-step3-stripe-link"
            >
              Open your Stripe onboarding →
            </a>
            <span className="ferro-muted">
              {' '}· expires{' '}
              {new Date(linkState.expiresAt).toLocaleString()}
            </span>
          </p>
        ) : null}

        {linkState.kind === 'error' ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step3-link-error"
          >
            {linkState.message}
          </span>
        ) : null}

        {value.accountLinkLastClickedAt ? (
          <p
            className="ferro-muted"
            data-testid="intake-step3-last-clicked"
          >
            You opened Stripe onboarding at{' '}
            {new Date(value.accountLinkLastClickedAt).toLocaleString()}.
          </p>
        ) : null}
      </section>

      <section
        className="ferro-field"
        aria-labelledby="intake-step3-status-heading"
      >
        <h2 id="intake-step3-status-heading" className="ferro-field__label">
          Account status
        </h2>
        <p className="ferro-muted">
          These values update automatically after Stripe confirms your account.
          You'll see them flip to <strong>active</strong> once onboarding
          completes.
        </p>
        <dl
          className="ferro-intake__statusGrid"
          data-testid="intake-step3-status-grid"
        >
          <div>
            <dt>Card payments</dt>
            <dd data-testid="intake-step3-cap-card">
              {value.capabilities.cardPayments || '—'}
            </dd>
          </div>
          <div>
            <dt>Transfers</dt>
            <dd data-testid="intake-step3-cap-transfers">
              {value.capabilities.transfers || '—'}
            </dd>
          </div>
          <div>
            <dt>Charges enabled</dt>
            <dd data-testid="intake-step3-charges-enabled">
              {value.chargesEnabled ? 'yes' : 'no'}
            </dd>
          </div>
          <div>
            <dt>Payouts enabled</dt>
            <dd data-testid="intake-step3-payouts-enabled">
              {value.payoutsEnabled ? 'yes' : 'no'}
            </dd>
          </div>
        </dl>
      </section>
    </form>
  );
}
